import _ from 'lodash';

import { WORKSHEET_COMPUTE_FUNCTION } from '../../components/WorksheetFieldsLayout/WorksheetFieldsLayout.constants';
import { parseWorksheetFieldSettings } from '../../components/WorksheetFieldsLayout/WorksheetFieldsLayout.utils';

const MONTHS_PER_YEAR = 12;
const PERCENT = 100;

const COMPUTE_ARGUMENTS_COUNT = {
  [WORKSHEET_COMPUTE_FUNCTION.PAYMENT]: 3,
};

/**
 * The level monthly payment that pays off `amount` over `termMonths` at an annual rate given in
 * percent (standard amortization, as a spreadsheet's PMT), rounded to cents. A 0% rate spreads the
 * amount evenly. Null when an input is missing or the term is not positive.
 *
 * @example
 * // CIS's revenue sheet: 206,497.46 at 9% over 300 months
 * calculateMonthlyPayment({ annualRatePercent: 9, termMonths: 300, amount: 206497.46 }); // 1732.92
 */
export const calculateMonthlyPayment = ({ annualRatePercent, termMonths, amount }) => {
  if (![annualRatePercent, termMonths, amount].every(_.isFinite) || termMonths <= 0) {
    return null;
  }

  const monthlyRate = annualRatePercent / PERCENT / MONTHS_PER_YEAR;
  const payment =
    monthlyRate === 0
      ? amount / termMonths
      : (amount * monthlyRate) / (1 - (1 + monthlyRate) ** -termMonths);

  return _.round(payment, 2);
};

/**
 * The worksheet's top-level fields with a known `compute:` setting (e.g. `compute: payment(Customer
 * Rate, Term, Note Amount)`), each with its parsed compute.
 */
export const getWorksheetComputedFields = fields =>
  fields
    .map(field => ({ ...field, compute: parseWorksheetFieldSettings(field.description).compute }))
    .filter(
      ({ compute }) => compute && COMPUTE_ARGUMENTS_COUNT[compute.name] === compute.args.length,
    );

const toNumber = value => {
  if (_.isNil(value) || value === '') {
    return null;
  }

  const number = Number(String(value).replace(/[$,\s]/g, ''));

  return _.isFinite(number) ? number : null;
};

// An argument names another answer of the worksheet, or else one of its calculated values.
const resolveArgument = ({ name, fieldsByName, form, calculatedValuesByName }) => {
  const field = fieldsByName[name];

  return field ? toNumber(form?.[field.id]) : toNumber(calculatedValuesByName[name]?.value);
};

/**
 * A computed field's value as the text the form holds, or null while an argument is unanswered.
 *
 * @param {{ compute: { name: string, args: string[] }, fieldsByName: Object, form: Object, calculatedValuesByName: Object }} params
 * @returns {string|null}
 */
export const computeWorksheetFieldValue = ({
  compute,
  fieldsByName,
  form,
  calculatedValuesByName,
}) => {
  if (compute.name !== WORKSHEET_COMPUTE_FUNCTION.PAYMENT) {
    return null;
  }

  const [annualRatePercent, termMonths, amount] = compute.args.map(name =>
    resolveArgument({ name, fieldsByName, form, calculatedValuesByName }),
  );
  const payment = calculateMonthlyPayment({ annualRatePercent, termMonths, amount });

  return _.isNull(payment) ? null : payment.toFixed(2);
};

export const buildWorksheetComputedHint = ({ args }) =>
  `Calculated: monthly payment on ${args[2]} at ${args[0]} over ${args[1]} months`;
